import axios from 'axios';
import * as Constants from './constants';
import authHeader from './auth-header';

const WARNING_KEY =
  'indexcaseExposureWarnings';

const EVIDENCE_KEY =
  'indexcaseDiseaseEvidence';

export const EVIDENCE_LEVELS = {
  SELF_REPORTED:
    'self_reported',
  DOCUMENT_UPLOADED:
    'document_uploaded',
  DOCUMENT_VERIFIED:
    'document_verified',
  CLINICIAN_CONFIRMED:
    'clinician_confirmed',
  LAB_CONFIRMED:
    'laboratory_confirmed',
};

export function getExposureWarnings() {
  try {
    const rows =
      JSON.parse(
        localStorage.getItem(
          WARNING_KEY
        )
      );

    return Array.isArray(rows)
      ? rows
      : [];
  } catch (_) {
    return [];
  }
}

export function storeExposureWarnings(
  warnings
) {
  const rows =
    Array.isArray(warnings)
      ? warnings
      : [];

  localStorage.setItem(
    WARNING_KEY,
    JSON.stringify(rows)
  );

  return rows;
}

export async function refreshExposureWarnings() {
  const headers =
    authHeader();

  if (!headers.Authorization) {
    return getExposureWarnings();
  }

  try {
    const response =
      await axios.get(
        Constants.API_URL +
          'exposureWarnings',
        { headers }
      );

    if (
      response &&
      Array.isArray(
        response.data
      )
    ) {
      return storeExposureWarnings(
        response.data
      );
    }
  } catch (_) {
    // Retain the last known authenticated warning cache.
  }

  return getExposureWarnings();
}

function storedEvidence() {
  try {
    const rows =
      JSON.parse(
        localStorage.getItem(
          EVIDENCE_KEY
        )
      );

    return Array.isArray(rows)
      ? rows
      : [];
  } catch (_) {
    return [];
  }
}

function storeDiseaseEvidence(
  rows
) {
  const next =
    Array.isArray(rows)
      ? rows
      : [];

  localStorage.setItem(
    EVIDENCE_KEY,
    JSON.stringify(next)
  );

  return next;
}

export function saveDiseaseEvidence(
  evidence
) {
  const items =
    storedEvidence();

  const record = {
    id:
      evidence.id ||
      `evidence-${Date.now()}`,
    diseaseId:
      evidence.diseaseId,
    diseaseName:
      evidence.diseaseName,
    level:
      evidence.level ||
      EVIDENCE_LEVELS.SELF_REPORTED,
    reportedAt:
      evidence.reportedAt ||
      new Date().toISOString(),
    verificationStatus:
      evidence.verificationStatus ||
      'unverified',
    evidenceReference:
      evidence.evidenceReference ||
      null,

    // A record created here remains on-device until
    // an explicit server submission workflow is used.
    serverBacked: false
  };

  storeDiseaseEvidence([
    ...items,
    record
  ]);

  return record;
}

export function getDiseaseEvidence() {
  return storedEvidence();
}

export async function refreshDiseaseEvidence() {
  const headers =
    authHeader();

  if (!headers.Authorization) {
    return getDiseaseEvidence();
  }

  const existing =
    getDiseaseEvidence();

  const localOnly =
    existing.filter(
      item =>
        item.serverBacked !== true &&
        typeof item.id === 'string' &&
        item.id.startsWith(
          'evidence-'
        )
    );

  const response =
    await axios.get(
      Constants.API_URL +
        'diseaseEvidence',
      { headers }
    );

  if (
    !response ||
    !Array.isArray(
      response.data
    )
  ) {
    throw new Error(
      'Disease evidence response was invalid.'
    );
  }

  const serverRows =
    response.data.map(
      item => ({
        ...item,
        serverBacked: true
      })
    );

  return storeDiseaseEvidence([
    ...serverRows,
    ...localOnly
  ]);
}

export async function submitDiseaseEvidenceToAccount(
  evidence
) {
  if (
    !evidence ||
    evidence.serverBacked
  ) {
    throw new Error(
      'Only a private on-device self-report can be submitted.'
    );
  }

  if (
    evidence.level !==
    EVIDENCE_LEVELS.SELF_REPORTED
  ) {
    throw new Error(
      'Only self-reported evidence can be submitted from this screen.'
    );
  }

  const diseaseId =
    Number(
      evidence.diseaseId
    );

  if (
    !Number.isInteger(
      diseaseId
    ) ||
    diseaseId < 1
  ) {
    throw new Error(
      'This self-report does not contain a valid disease.'
    );
  }

  const headers =
    authHeader();

  if (!headers.Authorization) {
    throw new Error(
      'Sign in before submitting a self-report to your IndexCase account.'
    );
  }

  const reportedAt =
    evidence.reportedAt ||
    new Date().toISOString();

  const response =
    await axios.post(
      Constants.API_URL +
        'diseaseEvidence',
      {
        diseaseId,
        level:
          EVIDENCE_LEVELS.SELF_REPORTED,
        reportedAt
      },
      {
        headers
      }
    );

  const serverId =
    response &&
    response.data
      ? Number(
          response.data.id
        )
      : NaN;

  if (
    !response ||
    response.status !== 201 ||
    !Number.isInteger(
      serverId
    )
  ) {
    throw new Error(
      'IndexCase did not confirm the self-report submission.'
    );
  }

  const cached =
    getDiseaseEvidence();

  const submitted = {
    id:
      serverId,
    diseaseId,
    diseaseName:
      evidence.diseaseName,
    level:
      EVIDENCE_LEVELS.SELF_REPORTED,
    verificationStatus:
      response.data.verificationStatus ||
      'unverified',
    issuer:
      null,
    reportedAt,
    verifiedAt:
      null,
    serverBacked:
      true
  };

  const fallback =
    storeDiseaseEvidence([
      ...cached.filter(
        item =>
          String(item.id) !==
            String(evidence.id) &&
          String(item.id) !==
            String(serverId)
      ),
      submitted
    ]);

  /*
   * Refresh from the authenticated backend when possible.
   * If that follow-up read fails, keep the confirmed server-backed
   * record in the local cache rather than reporting the POST as failed.
   */
  try {
    return await refreshDiseaseEvidence();
  } catch (_) {
    return fallback;
  }
}

export function warningActionText(
  warning
) {
  if (warning.action) {
    return warning.action;
  }

  if (
    warning.serious &&
    warning.symptomMatch
  ) {
    return (
      'Contact an appropriate healthcare service ' +
      'for medical assessment.'
    );
  }

  return (
    'Remain vigilant for the listed symptoms and ' +
    'follow local medical guidance if they develop.'
  );
}
